import { useSelector } from '@xstate/react';
import { useCallback } from 'react';
import { DeaLicenseFormValues } from '../dea/types';
import { selectDeaLicenseForm, selectDeaLicenses, selectLoading } from '../selectors';
import { useLicensesActorRef } from '../utils';

/**
 * Service hook for DEA license tab.
 * Subscribes to DEA-specific slices and shared loading state.
 * CDS license data changes don't trigger re-renders, but shared loading transitions may.
 */
export function useDeaService() {
  const licensesRef = useLicensesActorRef();

  // ── Narrow selectors ────────────────────────────────────────────────
  const deaLicenses = useSelector(licensesRef, selectDeaLicenses);
  const deaLicenseForm = useSelector(licensesRef, selectDeaLicenseForm);
  const loading = useSelector(licensesRef, selectLoading);

  // ── Stable callbacks ────────────────────────────────────────────────
  const triggerAdd = useCallback(() => licensesRef.send({ type: 'ON_ADD_LICENSE' }), [licensesRef]);

  const handleEditClick = useCallback(
    (id: number) => licensesRef.send({ type: 'ON_EDIT_LICENSE', id }),
    [licensesRef]
  );

  const handleDeleteClick = useCallback(
    (id: number) => licensesRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } }),
    [licensesRef]
  );

  const sendLicenseCreate = useCallback(
    (values: DeaLicenseFormValues) =>
      licensesRef.send({ type: 'ON_SUBMIT_LICENSE_CREATE', values }),
    [licensesRef]
  );

  const sendLicenseUpdate = useCallback(
    (values: DeaLicenseFormValues) =>
      licensesRef.send({ type: 'ON_SUBMIT_LICENSE_UPDATE', values }),
    [licensesRef]
  );

  const onCancel = useCallback(
    () => licensesRef.send({ type: 'ON_CANCEL_EDIT_OR_CREATE' }),
    [licensesRef]
  );

  return {
    deaLicenses,
    deaLicenseForm,
    loading,
    triggerAdd,
    handleEditClick,
    handleDeleteClick,
    sendLicenseCreate,
    sendLicenseUpdate,
    onCancel,
  };
}
